import { useCallback, useEffect, useRef, useState } from "react";
import type { ArtifactDto, ClientContextSuggestion, WorkflowResultEnvelope } from "../services/orchestratorApi";
import { orchestratorApi, buildAnalyzeEmailPayload } from "../services/orchestratorApi";
import { DialogAlreadyOpenError } from "../services/authClient";
import { logger } from "../obs/logger";
import { useSignalR, WORKFLOW_EVENT_METHOD, WF_EVENT_METHOD } from "../signalr/useSignalR";
import { workflowStore } from "../services/workflowStore";
import type { WorkflowEvent } from "../types/workflow";
import type { EmailContext } from "./useMailboxItem";
import { mergeStepProgress } from "@product-app/common/workflows/statusMerge";
import type { StepProgressCandidate } from "@product-app/common/workflows/statusMerge";

export type WorkflowPhase =
  | "idle"
  | "authenticating"
  | "analyzing"
  | "client_context"
  | "processing"
  | "plan_review"
  | "executing"
  | "completed"
  | "failed";

/**
 * Distinguishes where in the lifecycle a `failed` phase came from.
 *
 *  - `auth`    — failure during the pre-flight token acquisition (Office
 *                Dialog API + MSAL). The task pane should auto-close so
 *                we don't leave a dead pane sitting open after a sign-in
 *                problem the user can't recover from inline.
 *  - `process` — failure during the initial parse/plan call. Pane stays
 *                open so the user can read the error.
 *  - `execute` — failure after plan approval. Pane stays open.
 */
export type FailureContext = "auth" | "process" | "execute" | null;

export interface WorkflowStatus {
  step: string;
  message: string;
  timestamp: string;
}

/**
 * Live step progress fed by the substrate `wf_event` SignalR push.
 * Matches the `EmitPushInput` schema: step_index (0-based), total_steps,
 * step_id (catalog step identifier), completed (true once step_completed fires).
 */
export interface StepProgressState {
  stepIndex: number;
  totalSteps: number;
  currentStepId: string;
  displayName?: string | null;
  completed: boolean;
}

export interface EndorsementPlan {
  policy_number: FieldConfidence;
  client_name: FieldConfidence;
  line_of_business: FieldConfidence;
  change_type: FieldConfidence;
  effective_date: FieldConfidence;
  carrier: FieldConfidence;
  change_details: string;
  epic_client_id?: string;
  assigned_am?: string;
  current_coverage_summary?: string;
  carrier_portal_url?: string;
  endorsement_reference_id?: string;
  activity_reference_id?: string;
}

export interface FieldConfidence {
  value: unknown;
  confidence: number;
  source: string;
  needs_am_input: boolean;
}

// Substrate workflow lifecycle status constants (mirrors WorkflowStatus in Contracts.cs)
const WorkflowStatusValues = {
  Started: "started",
  Running: "running",
  PausedHitl: "paused_hitl",
  Completed: "completed",
  Cancelled: "cancelled",
  TimeoutCancelled: "timeout_cancelled",
  Failed: "failed",
} as const;

function describeError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Payload shape for the substrate `wf_event` SignalR method.
 * Mirrors `EmitPushInput` in the workflows substrate.
 */
interface WfEventPayload {
  instance_id: string;
  status: string;
  step_id?: string;
  step_index?: number;
  total_steps?: number;
  hitl_question?: unknown;
  error?: string;
}

/**
 * Hook managing the endorsement workflow lifecycle.
 *
 * Phases: idle → authenticating → processing → plan_review → executing →
 *         completed/failed
 *
 * The hook is endorsement-specific today. Once Daniel's substrate exposes
 * a generic launch + status contract, we'll widen this to drive any
 * workflow from `list_workflows` (plan §6.3, US-A11/A12).
 */
export function useWorkflowStatus() {
  const [phase, setPhase] = useState<WorkflowPhase>("idle");
  const [failureContext, setFailureContext] = useState<FailureContext>(null);
  const [workflowId, setWorkflowId] = useState<string>("");
  const [plan, setPlan] = useState<EndorsementPlan | null>(null);
  const [status, setStatus] = useState<WorkflowStatus[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [artifacts, setArtifacts] = useState<ArtifactDto[]>([]);
  const [suggestion, setSuggestion] = useState<ClientContextSuggestion | null>(null);
  const [stepProgress, setStepProgress] = useState<StepProgressState | null>(null);
  const [workflowResult, setWorkflowResult] = useState<WorkflowResultEnvelope | null>(null);
  // Memoize guard: fetch at most once per instance_id (AC-8 invariant).
  const resultFetchedForRef = useRef<string | null>(null);

  const addStatus = (step: string, message: string) => {
    setStatus((prev) => [
      ...prev,
      { step, message, timestamp: new Date().toISOString() },
    ]);
  };

  // Shared poll loop used by both confirmAndStart and legacy startWorkflow.
  const pollUntilTerminal = useCallback(async (id: string) => {
    for (;;) {
      let s: Awaited<ReturnType<typeof orchestratorApi.getStatus>>;
      try {
        s = await orchestratorApi.getStatus(id);
      } catch (err) {
        const message = describeError(err);
        setError(message);
        setPhase("failed");
        setFailureContext("process");
        addStatus("error", message);
        return;
      }

      if (
        s.status === WorkflowStatusValues.Completed ||
        s.status === "pending_plan_review"
      ) {
        if (s.plan) {
          setPlan(s.plan as unknown as EndorsementPlan);
          setPhase("plan_review");
          addStatus("plan_ready", "Plan ready for review");
          return;
        }
        try {
          const arts = await orchestratorApi.getArtifacts(id);
          setArtifacts(arts.artifacts ?? []);
        } catch {
          // Non-fatal
        }
        setPhase("completed");
        addStatus("completed", "Workflow completed");
        return;
      }

      if (s.status === WorkflowStatusValues.Failed || s.status === "failed") {
        setError(s.error ?? "Workflow failed");
        setPhase("failed");
        setFailureContext("process");
        addStatus("error", s.error ?? "Workflow failed");
        return;
      }

      if (s.status === WorkflowStatusValues.PausedHitl) {
        const hitlPlan = (s as unknown as { hitl_question?: { plan: EndorsementPlan } })
          ?.hitl_question?.plan;
        if (hitlPlan) setPlan(hitlPlan);
        setPhase("plan_review");
        addStatus("plan_ready", "Plan ready for review");
        return;
      }

      if (
        s.status === WorkflowStatusValues.Cancelled ||
        s.status === WorkflowStatusValues.TimeoutCancelled
      ) {
        setError("Workflow was cancelled");
        setPhase("failed");
        setFailureContext("process");
        addStatus("error", "Workflow was cancelled");
        return;
      }

      // Update step progress from poll response while still running.
      if (s.step_index != null && s.total_steps != null) {
        setStepProgress((prev) =>
          mergeStepProgress(prev, {
            stepIndex: s.step_index!,
            totalSteps: s.total_steps!,
            currentStepId: s.current_phase ?? "",
            displayName: (s as typeof s & { current_step_display_name?: string | null }).current_step_display_name ?? null,
            completed: false,
          }),
        );
      }
      addStatus(s.status, "Processing...");
      await sleep(1500);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Step 1: analyze the email and surface client context for confirmation.
  //
  // Per AC-2: orchestratorApi.analyzeEmailSync POSTs to /api/workflows/analyze_email
  // (the substrate's analyze_email MCP tool, NOT a workflow orchestration). This
  // path is unconditional — analyze_email is not gated by the
  // outlook_workflows_e2e_v2 flag; that flag only governs HITL handoff style and
  // SignalR-vs-polling on the monitor page (AC-18).
  const analyzeEmail = useCallback(async (emailContext: EmailContext) => {
    setError(null);
    setFailureContext(null);
    setArtifacts([]);
    setSuggestion(null);

    setPhase("authenticating");
    addStatus("auth", "Authorization in progress...");
    try {
      await orchestratorApi.primeAuth();
    } catch (err) {
      const message =
        err instanceof DialogAlreadyOpenError
          ? "Another sign-in is in progress in a different task pane. Please wait for it to finish, then try again."
          : describeError(err);
      setError(message);
      setPhase("failed");
      setFailureContext("auth");
      addStatus("auth_error", message);
      return;
    }

    setPhase("analyzing");
    addStatus("analyze_email", "Analyzing email...");

    // Shared builder → guarantees email_rest_id / internet_message_id /
    // mailbox_address (and every other field) ride every analyze path. See
    // orchestratorApi.buildAnalyzeEmailPayload.
    const emailPayload = buildAnalyzeEmailPayload(emailContext);

    try {
      const syncResult = await orchestratorApi.analyzeEmailSync(emailPayload);
      setSuggestion({
        client_name: syncResult.extraction.client_name,
        policy_number: syncResult.extraction.policy_number,
        client_email: syncResult.extraction.client_email,
        confidence: syncResult.extraction.confidence,
        recommended_workflow_id: syncResult.recommended_workflow_id,
        client_context: syncResult.client_context,
        // Carry the substrate's PDF/attachment text through so App.tsx can
        // forward it into triggerPrefill (workflow_prefill.attachment_text).
        attachment_text: syncResult.attachment_text ?? null,
        // Map the snake_case suggested_clients wire shape to the camelCase
        // ClientSearchResult the client tile renders. Drop any entry missing a
        // clientSk (can't be selected). Empty array → App.tsx falls through.
        suggestions: (syncResult.suggested_clients ?? [])
          .filter((s) => s.client_sk != null)
          .map((s) => ({
            clientSk: s.client_sk as number,
            clientName: s.client_name ?? "",
            branchName: s.branch_name ?? undefined,
            city: s.city ?? undefined,
            state: s.state ?? undefined,
          })),
      });
      setPhase("client_context");
      addStatus("client_context", "Review and confirm client details");
    } catch (err) {
      const message = describeError(err);
      setError(message);
      setPhase("failed");
      setFailureContext("process");
      addStatus("error", message);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollUntilTerminal]);

  // Step 2: user confirmed — fire the chosen carrier workflow.
  const confirmAndStart = useCallback(
    async (chosenWorkflowId: string, inputs: Record<string, unknown>) => {
      setPhase("processing");
      addStatus("start_workflow", "Starting workflow...");

      let instanceId: string;
      try {
        const result = await orchestratorApi.triggerCarrierWorkflow(chosenWorkflowId, inputs);
        instanceId = result.instance_id;
        setWorkflowId(instanceId);
      } catch (err) {
        const message = describeError(err);
        setError(message);
        setPhase("failed");
        setFailureContext("process");
        addStatus("error", message);
        return;
      }

      await pollUntilTerminal(instanceId);
    },
    [pollUntilTerminal],
  );

  const startWorkflow = useCallback(async (emailContext: EmailContext) => {
    setError(null);
    setFailureContext(null);
    setArtifacts([]);

    // Step 1 — prime the auth token. In real mode this triggers the Office
    // Dialog API + MSAL flow. In stub mode it's a no-op. We surface this as
    // its own phase so the UI shows "Authorization in progress…" rather
    // than the workflow stepper while the user is being signed in.
    setPhase("authenticating");
    addStatus("auth", "Authorization in progress...");
    try {
      await orchestratorApi.primeAuth();
    } catch (err) {
      const message =
        err instanceof DialogAlreadyOpenError
          ? "Another sign-in is in progress in a different task pane. Please wait for it to finish, then try again."
          : describeError(err);
      setError(message);
      setPhase("failed");
      setFailureContext("auth");
      addStatus("auth_error", message);
      return;
    }

    // Step 2 — trigger the workflow and start polling for status.
    setPhase("processing");
    addStatus("parse_email", "Parsing email content...");

    let instanceId: string;

    try {
      const result = await orchestratorApi.execute({
        email_subject: emailContext.subject,
        email_body: emailContext.body,
        email_sender: emailContext.sender,
        email_id: emailContext.emailId,
        email_received_date: emailContext.receivedDate,
        attachments: emailContext.attachments,
      });

      // The real API returns instance_id; stub returns both for compatibility.
      instanceId = result.instance_id ?? result.workflow_id ?? "";
      setWorkflowId(instanceId);
    } catch (err) {
      const message = describeError(err);
      setError(message);
      setPhase("failed");
      setFailureContext("process");
      addStatus("error", message);
      return;
    }

    // Step 3 — poll until terminal state.
    const poll = async (id: string) => {
      for (;;) {
        let s: Awaited<ReturnType<typeof orchestratorApi.getStatus>>;
        try {
          s = await orchestratorApi.getStatus(id);
        } catch (err) {
          const message = describeError(err);
          setError(message);
          setPhase("failed");
          setFailureContext("process");
          addStatus("error", message);
          return;
        }

        if (
          s.status === WorkflowStatusValues.Completed ||
          s.status === "pending_plan_review" // stub compatibility
        ) {
          if (s.plan) {
            setPlan(s.plan as unknown as EndorsementPlan);
            setPhase("plan_review");
            addStatus("plan_ready", "Plan ready for review");
            return;
          }
          // Completed without HITL — fetch artifacts.
          try {
            const arts = await orchestratorApi.getArtifacts(id);
            setArtifacts(arts.artifacts ?? []);
          } catch {
            // Non-fatal: artifacts are optional
          }
          setPhase("completed");
          addStatus("completed", "Workflow completed");
          return;
        }

        if (
          s.status === WorkflowStatusValues.Failed ||
          s.status === "failed"
        ) {
          setError(s.error ?? "Workflow failed");
          setPhase("failed");
          setFailureContext("process");
          addStatus("error", s.error ?? "Workflow failed");
          return;
        }

        if (s.status === WorkflowStatusValues.PausedHitl) {
          // HITL pause — surface the plan from hitl_question if present.
          const hitlPlan = (s as unknown as { hitl_question?: { plan: EndorsementPlan } })
            ?.hitl_question?.plan;
          if (hitlPlan) setPlan(hitlPlan);
          setPhase("plan_review");
          addStatus("plan_ready", "Plan ready for review");
          return;
        }

        if (
          s.status === WorkflowStatusValues.Cancelled ||
          s.status === WorkflowStatusValues.TimeoutCancelled
        ) {
          setError("Workflow was cancelled");
          setPhase("failed");
          setFailureContext("process");
          addStatus("error", "Workflow was cancelled");
          return;
        }

        // Still running — update stepper and wait before next poll.
        if (s.step_index != null && s.total_steps != null) {
          setStepProgress((prev) =>
            mergeStepProgress(prev, {
              stepIndex: s.step_index!,
              totalSteps: s.total_steps!,
              currentStepId: s.current_phase ?? "",
              displayName: (s as typeof s & { current_step_display_name?: string | null }).current_step_display_name ?? null,
              completed: false,
            }),
          );
        }
        addStatus(s.status, "Processing...");
        await sleep(1500);
      }
    };

    await poll(instanceId);
  }, []);

  const approvePlan = useCallback(
    async (corrections: Record<string, unknown>) => {
      if (!workflowId) return;
      setPhase("executing");
      addStatus("create_endorsement", "Plan approved — creating endorsement...");

      // Polls after resume until a terminal state is reached.
      const pollAfterResume = async (id: string) => {
        for (;;) {
          let s: Awaited<ReturnType<typeof orchestratorApi.getStatus>>;
          try {
            s = await orchestratorApi.getStatus(id);
          } catch (err) {
            const message = describeError(err);
            setError(message);
            setPhase("failed");
            setFailureContext("execute");
            addStatus("error", message);
            return;
          }

          if (s.status === WorkflowStatusValues.Completed || s.status === "completed") {
            try {
              const arts = await orchestratorApi.getArtifacts(id);
              setArtifacts(arts.artifacts ?? []);
            } catch {
              // Non-fatal
            }
            setPhase("completed");
            addStatus("completed", "Endorsement workflow completed");
            return;
          }

          if (s.status === WorkflowStatusValues.Failed || s.status === "failed") {
            setError(s.error ?? "Execution failed");
            setPhase("failed");
            setFailureContext("execute");
            addStatus("error", s.error ?? "Execution failed");
            return;
          }

          if (s.step_index != null && s.total_steps != null) {
            setStepProgress((prev) =>
              mergeStepProgress(prev, {
                stepIndex: s.step_index!,
                totalSteps: s.total_steps!,
                currentStepId: s.current_phase ?? "",
                displayName: (s as typeof s & { current_step_display_name?: string | null }).current_step_display_name ?? null,
                completed: false,
              }),
            );
          }
          addStatus(s.status, "Executing...");
          await sleep(1500);
        }
      };

      try {
        const result = await orchestratorApi.approve(workflowId, {
          approved: true,
          corrections,
        });

        if (
          result.status === WorkflowStatusValues.Completed ||
          result.status === "completed"
        ) {
          if (result.plan) setPlan(result.plan as unknown as EndorsementPlan);
          try {
            const instanceId = result.instance_id ?? result.workflow_id ?? workflowId;
            const arts = await orchestratorApi.getArtifacts(instanceId);
            setArtifacts(arts.artifacts ?? []);
          } catch {
            // Non-fatal
          }
          setPhase("completed");
          addStatus("completed", "Endorsement workflow completed");
        } else if (result.status === "failed") {
          setError(result.error ?? "Execution failed");
          setPhase("failed");
          setFailureContext("execute");
          addStatus("error", result.error ?? "Execution failed");
        } else {
          // Substrate returned an intermediate status after resume — poll again.
          const instanceId = result.instance_id ?? result.workflow_id ?? workflowId;
          await pollAfterResume(instanceId);
        }
      } catch (err) {
        const message = describeError(err);
        setError(message);
        setPhase("failed");
        setFailureContext("execute");
        addStatus("error", message);
      }
    },
    [workflowId],
  );

  const rejectPlan = useCallback(
    async (reason: string) => {
      if (!workflowId) return;
      setPhase("idle");
      addStatus("rejected", `Plan rejected: ${reason}`);

      await orchestratorApi.approve(workflowId, {
        approved: false,
        rejection_reason: reason,
      });
    },
    [workflowId],
  );

  // Wipes all workflow state and returns the panel to its initial
  // idle phase. Used as the auto-close fallback: when Outlook refuses
  // to programmatically dismiss the task pane (Office.addin.hide can
  // be missing or no-op depending on host build + manifest), we at
  // least give the user a clean slate so the next email starts a
  // fresh flow instead of re-rendering the completed/failed view.
  const resetWorkflow = useCallback(() => {
    setPhase("idle");
    setFailureContext(null);
    setWorkflowId("");
    setPlan(null);
    setStatus([]);
    setError(null);
    setArtifacts([]);
    setStepProgress(null);
    setWorkflowResult(null);
    resultFetchedForRef.current = null;
  }, []);

  // -------------------------------------------------------------------
  // SignalR live push — Agent E addition.
  //
  // When `workflowId` is non-empty (i.e. a substrate instance_id), open
  // the per-instance push channel via Agent A2's
  // /api/workflows/instances/{id}/push-channel and fold every
  // `workflowEvent` into the reactive workflowStore. The store IS the
  // source of truth for InstanceDetailDrawer; this
  // hook owns the React state for the single instance the launch flow
  // is currently driving.
  //
  // AC-3: also subscribe to the substrate `wf_event` method and dispatch on
  // `event.status`:
  //   step_started   → update stepProgress with {stepIndex, totalSteps, currentStepId}
  //   step_completed → mark the current step as completed
  //   paused_hitl    → transition state machine to plan_review (HITL gate)
  //   completed      → transition to "completed" and disconnect
  //   failed         → transition to "failed" and disconnect
  //   cancelled      → transition to "failed" (cancelled) and disconnect
  //
  // When SignalR is unavailable (`useSignalR.connect` short-circuits via
  // `_signalrUnavailable`), no live step progress is shown but
  // `pollUntilTerminal` continues to drive React state from HTTP — the
  // launch flow still terminates correctly.
  // -------------------------------------------------------------------
  const signalR = useSignalR();
  const lastSubscribedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!workflowId) return;
    if (lastSubscribedRef.current === workflowId) return;
    lastSubscribedRef.current = workflowId;

    let cancelled = false;
    let unsubscribeWorkflowEvent: (() => void) | null = null;
    let unsubscribeWfEvent: (() => void) | null = null;

    void (async () => {
      // Connect BEFORE subscribing so no events are missed (AC-3 invariant).
      await signalR.connect(workflowId);
      if (cancelled) return;

      // -- workflowEvent: store fan-out (existing behaviour) --
      unsubscribeWorkflowEvent = signalR.subscribe<WorkflowEvent>(WORKFLOW_EVENT_METHOD, (evt) => {
        if (!evt || !evt.instance_id) return;
        // Fold the substrate status_changed event into the reactive store.
        workflowStore.setState(evt.instance_id, {
          status: evt.status,
          current_phase: evt.current_phase ?? null,
          updated_at: evt.timestamp,
          error_message: evt.error ?? null,
        });
      });

      // -- wf_event: step progress + state machine (AC-3) --
      unsubscribeWfEvent = signalR.subscribe<WfEventPayload>(WF_EVENT_METHOD, (evt) => {
        if (!evt || !evt.instance_id) return;
        switch (evt.status) {
          case "step_started":
            setStepProgress((prev) =>
              mergeStepProgress(prev, {
                stepIndex: evt.step_index ?? 0,
                totalSteps: evt.total_steps ?? 0,
                currentStepId: evt.step_id ?? "",
                displayName: (evt as WfEventPayload & { step_display_name?: string | null }).step_display_name ?? null,
                completed: false,
              }),
            );
            break;
          case "step_completed":
            setStepProgress((prev) =>
              mergeStepProgress(prev, {
                stepIndex: evt.step_index ?? 0,
                totalSteps: evt.total_steps ?? 0,
                currentStepId: evt.step_id ?? (prev?.currentStepId ?? ""),
                displayName: (evt as WfEventPayload & { step_display_name?: string | null }).step_display_name ?? prev?.displayName ?? null,
                completed: true,
              }),
            );
            break;
          case "paused_hitl":
            addStatus("plan_ready", "Plan ready for review");
            setPhase("plan_review");
            break;
          case "completed":
            setPhase("completed");
            addStatus("completed", "Workflow completed");
            void signalR.disconnect(workflowId);
            break;
          case "failed": {
            const errMsg = evt.error ?? "Workflow failed";
            setError(errMsg);
            setPhase("failed");
            setFailureContext("execute");
            addStatus("error", errMsg);
            void signalR.disconnect(workflowId);
            break;
          }
          case "cancelled":
          case "timeout_cancelled": {
            setError("Workflow was cancelled");
            setPhase("failed");
            setFailureContext("execute");
            addStatus("error", "Workflow was cancelled");
            void signalR.disconnect(workflowId);
            break;
          }
          default:
            break;
        }
      });
    })();

    return () => {
      cancelled = true;
      if (unsubscribeWorkflowEvent) unsubscribeWorkflowEvent();
      if (unsubscribeWfEvent) unsubscribeWfEvent();
      // Don't disconnect here — other parts of the UI may still be
      // listening. We tear connections down on full hook unmount only.
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, signalR]);

  // Tear down everything on hook unmount so we don't leak SignalR
  // connections across sign-in/out cycles.
  useEffect(
    () => () => {
      void signalR.disconnect();
    },
    [signalR],
  );

  // -------------------------------------------------------------------
  // AC-8: on completion, fetch the final result envelope once per instance.
  // Memoized via resultFetchedForRef so repeated renders don't re-fetch.
  // -------------------------------------------------------------------
  useEffect(() => {
    if (phase !== "completed" || !workflowId) return;
    if (resultFetchedForRef.current === workflowId) return;
    resultFetchedForRef.current = workflowId;
    void (async () => {
      try {
        const envelope = await orchestratorApi.getWorkflowResult(workflowId);
        setWorkflowResult(envelope);
      } catch (err) {
        // Non-fatal: result fetch failures should not block the completion UI.
        logger.warn("getWorkflowResult failed (non-fatal)", { error: String(err) });
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, workflowId]);

  // -------------------------------------------------------------------
  // selectFromCatalog — Agent E addition.
  //
  // Called from the LaunchTab catalog click handler. Dispatches a new
  // workflow instance via the generalised triggerWorkflow surface
  // (Agent A) with a fresh idempotency_key. Transitions the hook from
  // `idle` (or any prior terminal state) to `processing` with the new
  // instance_id so the SignalR effect above immediately starts feeding
  // the store. Polling continues to drive React state for the launch
  // flow until SignalR's first push arrives.
  // -------------------------------------------------------------------
  const selectFromCatalog = useCallback(
    async (
      workflowIdToLaunch: string,
      clientSk: number | null,
      additionalInputs?: Record<string, unknown>,
    ) => {
      setError(null);
      setFailureContext(null);
      setArtifacts([]);
      setPlan(null);
      setStatus([]);
      setPhase("processing");
      addStatus("start_workflow", "Starting workflow...");

      // crypto.randomUUID is available in modern browsers and jsdom; we
      // tolerate fallback so older Outlook hosts don't crash before
      // the substrate sees the request.
      const idempotency_key =
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const inputs: Record<string, unknown> = {
        ...(clientSk != null ? { client_sk: clientSk } : {}),
        ...(additionalInputs ?? {}),
        idempotency_key,
      };

      let instanceId: string;
      try {
        const result = await orchestratorApi.triggerWorkflow(
          workflowIdToLaunch,
          inputs,
        );
        instanceId = result.instance_id;
        setWorkflowId(instanceId);
        // Pre-populate the reactive store so any UI watching for the
        // new instance immediately renders a "running" row rather than
        // waiting for the first SignalR push (which can lag under
        // 503-fallback / polling-only setups).
        workflowStore.setState(instanceId, {
          workflow_id: workflowIdToLaunch,
          status: result.status ?? "running",
          updated_at: new Date().toISOString(),
        });
      } catch (err) {
        const message = describeError(err);
        setError(message);
        setPhase("failed");
        setFailureContext("process");
        addStatus("error", message);
        return;
      }

      // Drive React state for the launch flow via the existing poll
      // loop. SignalR pushes update the store independently.
      await pollUntilTerminal(instanceId);
    },
    [pollUntilTerminal],
  );

  return {
    phase,
    failureContext,
    workflowId,
    plan,
    status,
    error,
    artifacts,
    suggestion,
    /** Live step progress from `wf_event` SignalR push (AC-3).
     *  Null until the first step_started event fires. */
    stepProgress,
    /** AC-8: final result envelope, fetched once on completion. Null until
     *  the workflow completes and the result fetch succeeds. */
    workflowResult,
    analyzeEmail,
    confirmAndStart,
    startWorkflow,
    approvePlan,
    rejectPlan,
    resetWorkflow,
    selectFromCatalog,
    /** Live SignalR connection status — surfaced for the
     *  "Reconnecting…" badge in the AppShell header (per §Gap 5). */
    isConnected: signalR.isConnected,
    isSignalRUnavailable: signalR.isSignalRUnavailable,
  };
}
